import { ReactNode } from "react";
import { noop } from "@common/helpers/function";
import { StatusFilter, StatusFilterType } from "@common/types/statusFilter";
import {
    FilterMultiselect,
    FilterMultiselectOption,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { Separator } from "@design-system/Separator/Separator";
import { VStack } from "@design-system/VStack/VStack";
import { ModeRadioGroup } from "../ReviewFilter/ModeRadioGroup";
import styles from "./CombinedStatusFilter.module.scss";

const MODE_OPTIONS: Array<{ value: StatusFilterType; label: string }> = [
    {
        value: StatusFilterType.ALL_SELECTED,
        label: "Every selected",
    },
    {
        value: StatusFilterType.ANY_SELECTED,
        label: "Any selected",
    },
    {
        value: StatusFilterType.EXCLUDE_SELECTED,
        label: "Exclude selected",
    },
];

interface Props<TStatus extends string> {
    name: string;
    statusFilter: StatusFilter<TStatus>;
    statuses: TStatus[];
    renderStatus: (status: TStatus) => ReactNode;
    onChange: (statusFilter: StatusFilter<TStatus>) => void;
}

export const CombinedStatusFilter = <TStatus extends string>({
    name,
    statusFilter,
    statuses,
    renderStatus,
    onChange,
}: Props<TStatus>) => {
    const toStatusOptions = (
        section: string,
    ): Array<FilterMultiselectOption<TStatus>> =>
        statuses.map((status) => ({
            value: status,
            label: renderStatus(status),
            searchableLabel: status,
            section,
        }));

    const update = (patch: Partial<StatusFilter<TStatus>>) => {
        onChange({ ...statusFilter, ...patch });
    };

    return (
        <VStack className={styles.filter}>
            <ModeRadioGroup
                name={name}
                value={statusFilter.filterType}
                options={MODE_OPTIONS}
                onChange={(filterType) => update({ filterType })}
            />
            <Separator />
            <FilterMultiselect
                inDropdown={false}
                scrollContainer="panel"
                searchable={false}
                options={toStatusOptions("Kick statuses")}
                value={statusFilter.kickStatuses}
                onChange={(kickStatuses) => update({ kickStatuses })}
                name="kickStatuses"
                onBlur={noop}
            />
            <FilterMultiselect
                inDropdown={false}
                scrollContainer="panel"
                searchable={false}
                options={toStatusOptions("External statuses")}
                value={statusFilter.externalStatuses}
                onChange={(externalStatuses) => update({ externalStatuses })}
                name="externalStatuses"
                onBlur={noop}
            />
        </VStack>
    );
};
